import { FeaturedItems } from '../../featured/featured.types'

export type PartnerConfigPartBaseResponse = {
  createdAt: string
  partnerId: string
}

export enum PartnerConfigKeys {
  DatePeriodConfig = 'date_period_configuration',
  HomePageBanner = 'home_page_banner_image',
  OrgSearchCheck = 'organisation_search_eligibility_check',
  GamificationDisplay = 'gamification_display',
  Regions = 'regions',
  Theme = 'theme',
  SupportedCurrencies = 'supported_currencies',
  OrgInTimeLog = 'require_organisation_time_log',
  HelpLink = 'help_link',
  PreferredCountries = 'preferred_countries',
  Featured = 'featured',
  HomePageBannerContent = 'home_page_banner_content',
  CalendarIntegration = 'calendar_integration',
  Logo = 'logo',
  Features = 'features',
  SpotlightedOrgs = 'spotlighted_orgs'
}

export type PartnerDatePeriodConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.DatePeriodConfig
  value: {
    date: string
    period: 'year'
  }
}

export type PartnerBannerImgConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.HomePageBanner
  value: string | null
}

export type PartnerOrgSearchEligibilityCheckConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.OrgSearchCheck
  value: boolean
}

export type PartnerGamificationDisplayConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.GamificationDisplay
  value: {
    body?: string
    title?: string
    displayAchievements?: boolean
  }
}

export type PartnerRegionsConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.Regions
  value: {
    [regionId: string]: {
      countryCodes: string[]
      id: string
      name: string
    }
  }
}

export type ThemeColors = {
  primary: string
  primaryHover: string
  primaryActive: string
  primaryFocus: string
  primary100: string
  primary400: string
  primary600: string
  info30: string
  info100: string
  info400: string
}

export type PartnerThemeConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.Theme
  partnerId: string
  value?: {
    colours: ThemeColors
    overlayGradientPercentage: number
  } | null
}

export type PartnerSupportedCurrenciesConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.SupportedCurrencies
  value: string[]
}

export type PartnerTimeLogOrgFieldConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.OrgInTimeLog
  value: boolean
}

export type PartnerHelpLinkConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.HelpLink
  value: {
    link?: string
    title?: string
  }
}

export type PartnerPreferredCountriesConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.PreferredCountries
  value: string[]
}

export type PartnerFeaturedConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.Featured
  value: FeaturedItems[]
}

export type PartnerHomePageBannerContentConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.HomePageBannerContent
  value: {
    welcomeCopy: boolean
    searchBar: boolean
    welcomeCopyTitle?: string | null
    welcomeCopySubtitle?: string | null
    welcomeCopyColor?: string | null
    bannerImage?: string | null
  }
}

export type PartnerCalendarIntegrationConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.CalendarIntegration
  value: string[]
}

export type PartnerLogoConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.Logo
  value: string | null
}

export type PartnerFeaturesConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.Features
  value: {
    profileSecondaryEmails: 'enabled' | 'disabled'
    opportunityTimeLogsRewardsEligibility?: 'enabled' | 'disabled'
  }
}

export type PartnerSpotlightedOrgsConfig = PartnerConfigPartBaseResponse & {
  key: PartnerConfigKeys.SpotlightedOrgs
  value: {
    organisationIds: string[]
    partnerLogo: string
  }
}

export type GetPartnerConfigResponse = {
  data: {
    [PartnerConfigKeys.DatePeriodConfig]: PartnerDatePeriodConfig
    [PartnerConfigKeys.HomePageBanner]: PartnerBannerImgConfig
    [PartnerConfigKeys.OrgSearchCheck]: PartnerOrgSearchEligibilityCheckConfig
    [PartnerConfigKeys.GamificationDisplay]: PartnerGamificationDisplayConfig
    [PartnerConfigKeys.Regions]: PartnerRegionsConfig
    [PartnerConfigKeys.Theme]: PartnerThemeConfig
    [PartnerConfigKeys.SupportedCurrencies]: PartnerSupportedCurrenciesConfig
    [PartnerConfigKeys.OrgInTimeLog]: PartnerTimeLogOrgFieldConfig
    [PartnerConfigKeys.HelpLink]: PartnerHelpLinkConfig
    [PartnerConfigKeys.PreferredCountries]: PartnerPreferredCountriesConfig
    [PartnerConfigKeys.Featured]: PartnerFeaturedConfig
    [PartnerConfigKeys.HomePageBannerContent]?: PartnerHomePageBannerContentConfig
    [PartnerConfigKeys.CalendarIntegration]?: PartnerCalendarIntegrationConfig
    [PartnerConfigKeys.Logo]?: PartnerLogoConfig
    [PartnerConfigKeys.Features]?: PartnerFeaturesConfig
    [PartnerConfigKeys.SpotlightedOrgs]?: PartnerSpotlightedOrgsConfig
  }
  object: string
}
